import {
  formatARS,
  PAYMENT_METHOD_LABELS,
  type ExpensesVsIncomePoint,
  type PaymentMethodStatDto,
  type SalesSeriesPoint,
  type StatsGranularity,
} from '@gl/shared';
import type { ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AXIS, axisARS, GRID, PAYMENT_COLORS, SERIES, SURFACE } from './chart-theme';
import { bucketLabel, bucketTitle } from './ranges';

const axisProps = {
  stroke: GRID,
  tick: { fill: AXIS, fontSize: 12 },
  tickLine: false,
  axisLine: false,
} as const;

function TooltipBox({ title, rows }: { title: string; rows: { color: string; label: string; value: string }[] }) {
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
      <p className="mb-1 font-medium">{title}</p>
      {rows.map((r) => (
        <p key={r.label} className="flex items-center gap-2">
          <span className="size-2 rounded-full" style={{ background: r.color }} />
          <span className="text-muted-foreground">{r.label}</span>
          <span className="ml-auto pl-3 font-medium tabular-nums">{r.value}</span>
        </p>
      ))}
    </div>
  );
}

export function LegendKey({ items }: { items: { color: string; label: string }[] }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {items.map((i) => (
        <span key={i.label} className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: i.color }} />
          {i.label}
        </span>
      ))}
    </div>
  );
}

export function EmptyChart({ children = 'Sin datos en este rango.' }: { children?: ReactNode }) {
  return <div className="flex h-56 items-center justify-center text-sm text-muted-foreground">{children}</div>;
}

export function SalesLineChart({ points, granularity }: { points: SalesSeriesPoint[]; granularity: StatsGranularity }) {
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={GRID} />
          <XAxis
            dataKey="bucket"
            {...axisProps}
            tickFormatter={(b: string) => bucketLabel(b, granularity)}
            minTickGap={24}
          />
          <YAxis {...axisProps} width={64} tickFormatter={axisARS} />
          <Tooltip
            cursor={{ stroke: AXIS, strokeWidth: 1 }}
            content={({ active, payload }) => {
              const p = active && payload?.[0]?.payload as SalesSeriesPoint | undefined;
              if (!p) return null;
              return (
                <TooltipBox
                  title={bucketTitle(p.bucket, granularity)}
                  rows={[
                    { color: SERIES.sales, label: 'Ventas', value: formatARS(p.salesTotalCents) },
                    { color: 'transparent', label: 'Tickets', value: String(p.salesCount) },
                  ]}
                />
              );
            }}
          />
          <Area
            type="monotone"
            dataKey="salesTotalCents"
            name="Ventas"
            stroke={SERIES.sales}
            strokeWidth={2}
            fill={SERIES.sales}
            fillOpacity={0.1}
            dot={false}
            activeDot={{ r: 5, stroke: SURFACE, strokeWidth: 2, fill: SERIES.sales }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ExpensesIncomeChart({
  points,
  granularity,
}: {
  points: ExpensesVsIncomePoint[];
  granularity: StatsGranularity;
}) {
  return (
    <div className="grid gap-2">
      <LegendKey
        items={[
          { color: SERIES.expenses, label: 'Gastos' },
          { color: SERIES.incomes, label: 'Ingresos' },
        ]}
      />
      <div className="h-60">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barGap={2}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis
              dataKey="bucket"
              {...axisProps}
              tickFormatter={(b: string) => bucketLabel(b, granularity)}
              minTickGap={24}
            />
            <YAxis {...axisProps} width={64} tickFormatter={axisARS} />
            <Tooltip
              cursor={{ fill: GRID, opacity: 0.5 }}
              content={({ active, payload }) => {
                const p = active && payload?.[0]?.payload as ExpensesVsIncomePoint | undefined;
                if (!p) return null;
                return (
                  <TooltipBox
                    title={bucketTitle(p.bucket, granularity)}
                    rows={[
                      { color: SERIES.expenses, label: 'Gastos', value: formatARS(p.expensesTotalCents) },
                      { color: SERIES.incomes, label: 'Ingresos', value: formatARS(p.incomesTotalCents) },
                    ]}
                  />
                );
              }}
            />
            <Bar dataKey="expensesTotalCents" name="Gastos" fill={SERIES.expenses} radius={[4, 4, 0, 0]} maxBarSize={24} />
            <Bar dataKey="incomesTotalCents" name="Ingresos" fill={SERIES.incomes} radius={[4, 4, 0, 0]} maxBarSize={24} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function PaymentMethodsChart({ methods, totalCents }: { methods: PaymentMethodStatDto[]; totalCents: number }) {
  const used = methods.filter((m) => m.totalCents > 0);
  if (used.length === 0) return <EmptyChart />;
  return (
    <div className="grid items-center gap-4 sm:grid-cols-[180px_1fr]">
      <div className="relative mx-auto h-44 w-44">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={used}
              dataKey="totalCents"
              nameKey="paymentMethod"
              innerRadius="62%"
              outerRadius="100%"
              paddingAngle={0}
              stroke={SURFACE}
              strokeWidth={2}
              isAnimationActive={false}
            >
              {used.map((m) => (
                <Cell key={m.paymentMethod} fill={PAYMENT_COLORS[m.paymentMethod]} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                const m = active && payload?.[0]?.payload as PaymentMethodStatDto | undefined;
                if (!m) return null;
                return (
                  <TooltipBox
                    title={PAYMENT_METHOD_LABELS[m.paymentMethod]}
                    rows={[
                      { color: PAYMENT_COLORS[m.paymentMethod], label: `${m.sharePct}%`, value: formatARS(m.totalCents) },
                    ]}
                  />
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xs text-muted-foreground">Total</span>
          <span className="text-sm font-semibold tabular-nums">{formatARS(totalCents)}</span>
        </div>
      </div>
      <ul className="grid gap-1.5 text-sm">
        {used.map((m) => (
          <li key={m.paymentMethod} className="flex items-center gap-2">
            <span className="size-2.5 shrink-0 rounded-sm" style={{ background: PAYMENT_COLORS[m.paymentMethod] }} />
            <span className="flex-1">{PAYMENT_METHOD_LABELS[m.paymentMethod]}</span>
            <span className="w-12 text-right text-muted-foreground tabular-nums">{m.sharePct}%</span>
            <span className="w-24 text-right font-medium tabular-nums">{formatARS(m.totalCents)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
